import {
  AfterViewInit,
  Component,
  DoCheck,
  Input,
  OnInit
} from '@angular/core';

import {
  BlockUI,
  NgBlockUI
} from 'ng-block-ui';

import { TranslateService } from '@ngx-translate/core';

import {
  HaynesProVesaFaultCodeComponentModel,
  HaynesProVesaFaultCodeDiagnosticDataModel
} from '../typings/haynes-pro.vesa';

@Component({
  styles: [ require('./haynes-pro.diagnostic-tool.vesa-fault-codes.expanded-row.component.scss') ],
  template: require('./haynes-pro.diagnostic-tool.vesa-fault-codes.expanded-row.component.html')
})
export class HaynesProDiagnosticToolVesaFaultCodesExpandedRowComponent implements AfterViewInit, DoCheck, OnInit {

  @BlockUI('haynesProVesaFaultCodesRowBlock') block : NgBlockUI;

  @Input() onShowDiagnosis  : ( component : HaynesProVesaFaultCodeComponentModel ) => void;
  @Input() record           : any;
  @Input() rowExpansionData : any;
  @Input() selectedSystems  : Array<{
    description              ?: string;
    group_text               ?: string;
    system_type_id           ?: string;
    wiring_diagram_system_id ?: string;
  }> = [];
  @Input() vehicleId        : number;

  get components () : Array<HaynesProVesaFaultCodeComponentModel> {
    return this.filterBySelectedSystems(this.data?.components || []);
  }

  get componentSections () : Array<{
    label      : string;
    components : Array<{ data : HaynesProVesaFaultCodeComponentModel; index : number }>;
  }> {
    const filtered = this.components;

    if (!filtered.length) {
      return [];
    }

    const byId = new Map<string, Array<HaynesProVesaFaultCodeComponentModel>>();

    filtered.forEach(( comp : HaynesProVesaFaultCodeComponentModel ) => {
      const key      = comp.system_type_id;
      const existing = byId.get(key);

      if (existing) {
        existing.push(comp);
      } else {
        byId.set(key, [ comp ]);
      }
    });

    let counter = 1;
    const sections : Array<{
      label      : string;
      components : Array<{ data : HaynesProVesaFaultCodeComponentModel; index : number }>;
    }> = [];

    (this.selectedSystems || []).forEach(sel => {
      const sysId = sel?.system_type_id || sel?.wiring_diagram_system_id;
      const comps = sysId ? byId.get(sysId) : undefined;

      if (!comps?.length) {
        return;
      }

      sections.push({
        label      : this.buildSystemLabel(sel),
        components : comps.map(c => ({ data: c, index: counter++ }))
      });
    });

    return sections;
  }

  get data () : HaynesProVesaFaultCodeDiagnosticDataModel {
    return this.record?.__diagnosticData;
  }

  get fallbackText () : string {
    return (this.data?.fault_code_text || this.data?.component_text || '').trim();
  }

  get hasComponents () : boolean {
    return this.components.length > 0;
  }

  get hasNoData () : boolean {
    return !this.hasComponents && !this.fallbackText;
  }

  get hasTextOnly () : boolean {
    return !this.hasComponents && !!this.fallbackText;
  }

  get isLoading () : boolean {
    return !this.record?.__activated;
  }

  get relatedFaultCodes () : string {
    const codes = (this.data?.fault_codes || [])
      .map(fc => fc?.fault_code)
      .filter(Boolean);

    return Array.from(new Set(codes)).join(', ');
  }

  constructor (public translateService : TranslateService) {}

  ngAfterViewInit () : void {
    if (this.isLoading) {
      this.block.start();
    }
  }

  ngDoCheck () : void {
    if (!this.isLoading && this.block.isActive) {
      this.block.stop();
    }
  }

  ngOnInit () : void {
    if (typeof this.rowExpansionData === 'function') {
      this.rowExpansionData(this.record);
    }
  }

  handleShowDiagnosis ( component : HaynesProVesaFaultCodeComponentModel ) : void {
    if (typeof this.onShowDiagnosis === 'function') {
      this.onShowDiagnosis(component);
    }
  }

  private filterBySelectedSystems (
    components : Array<HaynesProVesaFaultCodeComponentModel>
  ) : Array<HaynesProVesaFaultCodeComponentModel> {
    if (!components.length) {
      return components;
    }

    const selectedIds = new Set(
      (this.selectedSystems || [])
        .map(s => s?.system_type_id || s?.wiring_diagram_system_id)
        .filter(( id ) : id is string => !!id)
    );

    if (!selectedIds.size) {
      return components;
    }

    return components.filter(c => !!c.system_type_id && selectedIds.has(c.system_type_id));
  }

  private buildSystemLabel (
    sel : { description ?: string; group_text ?: string }
  ) : string {
    const parts = [ sel?.group_text, sel?.description ].filter(Boolean);

    return parts.join(' — ');
  }
}
